import { Expense, ExpensePriority } from "./types";
import { getAvailableSeriesRoots } from "./expenseSeriesUtils";
import {
  getEffectiveExpenseAmount,
  ExpenseAmountContext,
} from "./expenseUtils";

// A single recurring line, resolved to its real per-occurrence charge and a
// normalized cadence. This view deliberately ignores due dates / overdue state
// — it answers "what are my normal recurring commitments?", not "what is late?".
export interface RecurringBillLine {
  id: string;
  name: string;
  subName?: string; // secondary label shown smaller/faded under the name
  amount: number; // real per-occurrence charge
  pattern: string; // raw recurrencePattern as stored
  frequency: BillFrequency;
  priority: PriorityFilterValue; // "unassigned" when none set
  kind: "bill" | "debt";
  isPostponed: boolean; // temporarily not being paid (e.g. covered elsewhere)
  linkedDebtId?: string; // set when this bill's amount is driven by a linked debt
  priorityRank: number | null; // numeric rank for the priority board; null = unranked
}

export type BillFrequency =
  | "weekly"
  | "biweekly"
  | "monthly"
  | "quarterly"
  | "semiannual"
  | "annually";

// The filterable priority values: the real priorities plus "unassigned" for
// bills that have not been tagged yet.
export type PriorityFilterValue = ExpensePriority | "unassigned";

export interface RecurringBillGroup {
  frequency: BillFrequency;
  label: string;
  occurrencesPerYear: number;
  lines: RecurringBillLine[];
  perOccurrenceSubtotal: number; // sum of each bill's charge, one occurrence
  monthlySubtotal: number; // sum folded into a monthly figure
  annualSubtotal: number; // sum × occurrencesPerYear
}

// Charges per MONTH for each cadence — the basis for folding every bill into a
// common monthly figure. Weekly counts as 4/mo (four weeks), quarterly as 1/3
// (one charge every three months), etc., per the product rule for the
// cumulative total. Yearly figures derive from these (× 12).
const OCCURRENCES_PER_MONTH: Record<BillFrequency, number> = {
  weekly: 4,
  biweekly: 2,
  monthly: 1,
  quarterly: 1 / 3,
  semiannual: 1 / 6,
  annually: 1 / 12,
};

const OCCURRENCES_PER_YEAR: Record<BillFrequency, number> = {
  weekly: 4 * 12,
  biweekly: 2 * 12,
  monthly: 12,
  quarterly: 4,
  semiannual: 2,
  annually: 1,
};

const FREQUENCY_LABEL: Record<BillFrequency, string> = {
  weekly: "Weekly",
  biweekly: "Biweekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semiannual: "Semiannual",
  annually: "Annually",
};

// Display order: the three the user asked for lead, the rest follow so nothing
// is hidden if it exists in the data.
const FREQUENCY_ORDER: BillFrequency[] = [
  "monthly",
  "quarterly",
  "annually",
  "weekly",
  "biweekly",
  "semiannual",
];

// Normalize the free-form recurrencePattern to a canonical cadence, mirroring
// the aliases the projection reader accepts (weekly/annually/yearly, etc.).
export const normalizeFrequency = (pattern?: string): BillFrequency => {
  const p = (pattern || "").toLowerCase().trim();
  if (p === "weekly") return "weekly";
  if (p === "biweekly" || p === "bi-weekly") return "biweekly";
  if (p === "quarterly") return "quarterly";
  if (p === "semiannual" || p === "semi-annual" || p === "every_6_months")
    return "semiannual";
  if (p === "annually" || p === "annual" || p === "yearly") return "annually";
  return "monthly"; // default cadence, matches the projection reader
};

export const occurrencesPerYear = (frequency: BillFrequency): number =>
  OCCURRENCES_PER_YEAR[frequency];

export const occurrencesPerMonth = (frequency: BillFrequency): number =>
  OCCURRENCES_PER_MONTH[frequency];

const priorityOf = (priority?: ExpensePriority | null): PriorityFilterValue =>
  priority || "unassigned";

// Build the recurring-bill lines from the raw expense list: recurring series
// roots only, each resolved to its effective charge. Bills with a non-positive
// amount are dropped so budgets/placeholders don't clutter the view.
// The recurring monthly amount stored on a debt (absolute value — debts may be
// stored as negative outflows), or 0 when the debt has no recurring amount.
export const debtRecurringAmount = (debt: any): number =>
  Math.abs(Number(debt?.maxMonthlyPayment ?? debt?.max_monthly_payment ?? 0));

export const buildRecurringBillLines = (
  expenses: Expense[],
  debts?: any[],
  context?: ExpenseAmountContext
): RecurringBillLine[] =>
  getAvailableSeriesRoots(expenses)
    .map((e) => {
      let amount = getEffectiveExpenseAmount(e, debts, context);
      let name = e.name;
      let subName: string | undefined;
      let postponed = isPostponedNow(e.isPostponed, e.postponedUntil);
      // An expense linked to a debt IS that debt: its amount is driven by the
      // debt's recurring amount, it shows the debt's creditor (primary) with the
      // account name underneath, and it inherits the debt's postponed state.
      if (e.linkedDebtId && debts) {
        const linked = debts.find(
          (d) => String(d.id) === String(e.linkedDebtId)
        );
        if (linked) {
          const debtAmount = debtRecurringAmount(linked);
          if (debtAmount > 0) amount = debtAmount;
          const creditor = linked.creditor || linked.account || e.name;
          const account = linked.account || "";
          name = creditor;
          subName = account && account !== creditor ? account : undefined;
          if (isDebtPostponed(linked)) postponed = true;
        }
      }
      return {
        id: e.id,
        name,
        subName,
        amount,
        pattern: e.recurrencePattern || "monthly",
        frequency: normalizeFrequency(e.recurrencePattern),
        priority: priorityOf(e.priority),
        kind: "bill" as const,
        isPostponed: postponed,
        linkedDebtId: e.linkedDebtId,
        priorityRank:
          e.recurringPriorityRank === undefined
            ? null
            : e.recurringPriorityRank,
      };
    })
    .filter((line) => line.amount > 0);

// Extract the raw debt id from a debt line's id ("debt-<id>" → "<id>").
export const debtIdOfLine = (lineId: string): string =>
  lineId.replace(/^debt-/, "");

// Build monthly lines from debts (each carries a monthly payment). Every debt
// with a positive monthly payment is included; the debt is the source of truth,
// so summarize drops any recurring bill that merely mirrors a shown debt. Debts
// are treated as Essential obligations for filtering.
export const buildDebtLines = (
  debts: any[] | undefined
): RecurringBillLine[] => {
  if (!debts || debts.length === 0) return [];
  return debts
    .map((d) => {
      const monthly = debtRecurringAmount(d);
      // Creditor is the primary label; the account name goes underneath (faded).
      const creditor = d.creditor || d.account || "Debt";
      const account = d.account || "";
      const subName = account && account !== creditor ? account : undefined;
      return {
        id: `debt-${d.id}`,
        name: creditor,
        subName,
        amount: monthly,
        pattern: "monthly",
        frequency: "monthly" as BillFrequency,
        priority: "essential" as PriorityFilterValue,
        kind: "debt" as const,
        isPostponed: isDebtPostponed(d),
        priorityRank: null,
      };
    })
    .filter((line) => line.amount > 0);
};

// Currently postponed when flagged AND either indefinite (no end date) or the
// end date is still in the future.
export const isPostponedNow = (
  flag?: boolean | null,
  until?: string | Date | null
): boolean => {
  if (!flag) return false;
  if (!until) return true; // indefinite
  const end = new Date(until);
  return isNaN(end.getTime()) ? true : end.getTime() > Date.now();
};

// A debt is currently postponed (handles snake/camel field names).
export const isDebtPostponed = (debt: any): boolean =>
  isPostponedNow(
    debt?.isPostponed ?? debt?.is_postponed,
    debt?.postponedUntil ?? debt?.postponed_until ?? null
  );

// Group the lines by cadence, in display order, with per-occurrence, monthly and
// annualized subtotals for each group.
export const groupRecurringBills = (
  lines: RecurringBillLine[]
): RecurringBillGroup[] => {
  const byFrequency = new Map<BillFrequency, RecurringBillLine[]>();
  for (const line of lines) {
    const list = byFrequency.get(line.frequency) || [];
    list.push(line);
    byFrequency.set(line.frequency, list);
  }
  const groups: RecurringBillGroup[] = [];
  for (const frequency of FREQUENCY_ORDER) {
    const groupLines = byFrequency.get(frequency);
    if (!groupLines || groupLines.length === 0) continue;
    const perYear = OCCURRENCES_PER_YEAR[frequency];
    const perMonth = OCCURRENCES_PER_MONTH[frequency];
    const sortedLines = [...groupLines].sort((a, b) => b.amount - a.amount);
    groups.push({
      frequency,
      label: FREQUENCY_LABEL[frequency],
      occurrencesPerYear: perYear,
      lines: sortedLines,
      perOccurrenceSubtotal: sortedLines.reduce((s, l) => s + l.amount, 0),
      monthlySubtotal: sortedLines.reduce((s, l) => s + l.amount * perMonth, 0),
      annualSubtotal: sortedLines.reduce((s, l) => s + l.amount * perYear, 0),
    });
  }
  return groups;
};

export interface RecurringBillsOptions {
  debts?: any[];
  context?: ExpenseAmountContext;
  includeDebts?: boolean;
  // When false (default), postponed bills are excluded from the view.
  includePostponed?: boolean;
  // When non-empty, only lines whose priority is in the set are shown.
  priorityFilter?: PriorityFilterValue[] | null;
  // Free-text filter matched (case-insensitive) against the line name.
  search?: string;
}

export interface RecurringBillsSummary {
  groups: RecurringBillGroup[];
  monthlyTotal: number; // every line folded into a monthly figure and summed
  annualTotal: number; // monthlyTotal × 12
  billCount: number; // lines shown after filtering
  totalCount: number; // lines available before the priority filter
}

// One call for the dialog: raw expenses (+ optional debts/filter) in, everything
// the view needs out. Cumulative totals fold every cadence into a monthly figure
// (weekly ×4, quarterly ÷3, annually ÷12, …).
export const summarizeRecurringBills = (
  expenses: Expense[],
  options: RecurringBillsOptions = {}
): RecurringBillsSummary => {
  const {
    debts,
    context,
    includeDebts,
    includePostponed,
    priorityFilter,
    search,
  } = options;
  const billLines = buildRecurringBillLines(expenses, debts, context);
  // A debt-linked expense IS a debt obligation — the two are the same thing.
  // So debt obligations (debt-linked expenses AND debts with a recurring amount)
  // appear ONLY when "Include debts" is on, and each appears exactly once.
  const plainBills = billLines.filter((b) => !b.linkedDebtId);
  const debtLinkedBills = billLines.filter((b) => b.linkedDebtId);

  let allLines: RecurringBillLine[];
  if (includeDebts) {
    const debtLines = buildDebtLines(debts);
    // A debt already represented by a linked expense is shown via that expense
    // (its canonical, editable form); the rest of the recurring debts show as
    // debt lines so every debt with a recurring amount appears exactly once.
    const debtIdsWithExpense = new Set(
      debtLinkedBills.map((b) => b.linkedDebtId as string)
    );
    const debtLinesWithoutExpense = debtLines.filter(
      (d) => !debtIdsWithExpense.has(debtIdOfLine(d.id))
    );
    allLines = [...plainBills, ...debtLinkedBills, ...debtLinesWithoutExpense];
  } else {
    // Debts hidden → debt-linked expenses are hidden too (they are debts).
    allLines = [...plainBills];
  }

  const query = (search || "").trim().toLowerCase();
  const filtered = allLines
    .filter((l) => (includePostponed ? true : !l.isPostponed))
    .filter((l) =>
      priorityFilter && priorityFilter.length > 0
        ? priorityFilter.includes(l.priority)
        : true
    )
    .filter((l) => (query ? l.name.toLowerCase().includes(query) : true));

  const groups = groupRecurringBills(filtered);
  const monthlyTotal = groups.reduce((s, g) => s + g.monthlySubtotal, 0);
  return {
    groups,
    monthlyTotal,
    annualTotal: monthlyTotal * 12,
    billCount: filtered.length,
    totalCount: allLines.length,
  };
};
